
/* =========================================
   WAMBOLT SYSTEM PART INTERACTIONS
   =========================================
   Loaded once in layout/theme.liquid to support product sections
   reached through Barba navigation. The section keeps its existing
   Shopify and Wambolt lifecycle events.
*/

/* =========================================
   MEDIA SETUP
   ========================================= */

function initMediaSetup() {
  const mediaElements = document.querySelectorAll(
    '.wambolt-system-part [data-media-init]'
  );

  initMediaSetup._cleanup?.forEach(fn => fn());
  initMediaSetup._cleanup = [];

  if (!mediaElements.length) return;

  const pauseDelay = 0;
  const viewportOffset = 0.1;

  const isHoverDevice = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  ).matches;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const isMobile = window.matchMedia(
    '(max-width: 749px)'
  ).matches;

  const cleanupFns = [];
  const rootMarginValue = viewportOffset * 100;

  mediaElements.forEach(mediaEl => {
    const video = mediaEl.querySelector('[data-media-video-src]');
    if (!video) return;

    const mode = mediaEl.dataset.mediaMode || 'autoplay';
    const touchMode = mediaEl.dataset.mediaTouchMode;
    const resetAttr = mediaEl.dataset.mediaReset;
    const pausedStatusAttr = mediaEl.dataset.mediaOnPause;

    const toggleElements = [
      ...mediaEl.querySelectorAll('[data-media-toggle]')
    ];

    const activeMode = reducedMotion
      ? 'click'
      : isMobile
        ? 'autoplay'
        : !isHoverDevice
          ? (touchMode || (mode === 'hover' ? 'autoplay' : mode))
          : mode;

    const shouldResetOnPause =
      resetAttr === 'true'
        ? true
        : resetAttr === 'false'
          ? false
          : activeMode === 'hover';

    const pausedStatus =
      pausedStatusAttr === 'paused'
        ? 'paused'
        : 'not-active';

    const clickTargets = toggleElements.length
      ? toggleElements
      : [mediaEl];

    const shouldUseClickToggle = toggleElements.length > 0;

    let isInView = false;
    let isHovering = false;
    let hasLoaded = false;
    let userPaused = false;
    let userActivated = false;
    let shouldBePlaying = false;
    let pauseTimer = null;
    let hasWarnedPlaybackError = false;

    const warnPlaybackError = error => {
      if (hasWarnedPlaybackError) return;
      hasWarnedPlaybackError = true;

      console.warn(
        '[Wambolt System Part] Video playback failed:',
        video.currentSrc || video.dataset.mediaVideoSrc,
        error
      );
    };

    const setStatus = status => {
      mediaEl.dataset.mediaStatus = status;

      const playing = status === 'playing' || status === 'loading';

      toggleElements.forEach(toggle => {
        const name = toggle.dataset.motionLabel || 'product';

        toggle.setAttribute(
          'aria-label',
          `${playing ? 'Pause' : 'Play'} ${name} animation`
        );

        toggle.setAttribute('aria-pressed', String(playing));
      });
    };

    const clearPauseTimer = () => {
      clearTimeout(pauseTimer);
    };

    const addCleanup = fn => {
      cleanupFns.push(fn);
    };

    const on = (target, event, handler) => {
      target.addEventListener(event, handler);

      addCleanup(() => {
        target.removeEventListener(event, handler);
      });
    };

    const playAttempt = () => {
      video.play()
        .then(() => {
          if (shouldBePlaying) {
            setStatus('playing');
          }
        })
        .catch(error => {
          // Aborted play attempts are normal if someone hovers briefly
          // or Barba cleans up while the media is still loading.
          if (
            shouldBePlaying &&
            !document.hidden &&
            error?.name !== 'AbortError'
          ) {
            warnPlaybackError(error);
          }
        });
    };

    const loadVideo = () => {
      if (hasLoaded) return;

      const src = video.dataset.mediaVideoSrc;
      if (!src) return;

      video.muted = true;
      video.playsInline = true;

      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      video.preload = 'auto';
      video.src = src;
      video.load();

      hasLoaded = true;
    };

    const shouldResume = () => {
      if (!isInView || document.hidden) return false;

      if (activeMode === 'autoplay') {
        return !userPaused;
      }

      if (activeMode === 'click') {
        return userActivated && !userPaused;
      }

      return isHovering && !userPaused;
    };

    const playVideo = () => {
      if (document.hidden) return;

      shouldBePlaying = true;
      clearPauseTimer();
      loadVideo();

      setStatus(
        video.readyState < 3 ? 'loading' : 'playing'
      );

      playAttempt();
    };

    const pauseVideo = (delay = 0, reset = false) => {
      shouldBePlaying = false;
      clearPauseTimer();

      const doPause = () => {
        video.pause();

        if (reset && video.readyState > 0) {
          video.currentTime = 0;
        }
      };

      if (delay > 0) {
        pauseTimer = setTimeout(doPause, delay);
      } else {
        doPause();
      }
    };

    const handleHoverIn = () => {
      if (!isInView || document.hidden) return;

      if (mediaEl.closest(
        '.wambolt-system-part__detail-grid.is-dragging'
      )) {
        return;
      }

      isHovering = true;
      clearPauseTimer();

      if (userPaused) return;

      if (!video.paused) {
        shouldBePlaying = true;
        setStatus('playing');
        return;
      }

      playVideo();
    };

    const handleHoverOut = () => {
      if (!isInView) return;

      isHovering = false;
      userPaused = false;
      setStatus(pausedStatus);

      pauseVideo(pauseDelay, shouldResetOnPause);
    };

    const handleClick = () => {
      if (document.hidden) return;

      clearPauseTimer();

      if (!shouldBePlaying) {
        userActivated = true;
        userPaused = false;
        playVideo();
      } else {
        userActivated = true;
        userPaused = true;
        setStatus(pausedStatus);

        pauseVideo(pauseDelay, shouldResetOnPause);
      }
    };

    if (shouldUseClickToggle) {
      clickTargets.forEach(toggleEl => {
        on(toggleEl, 'pointerdown', event => {
          event.stopPropagation();
        });

        on(toggleEl, 'click', event => {
          event.preventDefault();
          event.stopPropagation();
          handleClick();
        });
      });
    }

    const handleViewport = entries => {
      entries.forEach(entry => {
        if (entry.target !== mediaEl) return;

        isInView = isMobile
          ? entry.isIntersecting && entry.intersectionRatio >= 0.5
          : entry.isIntersecting;

        if (isInView) {
          if (activeMode === 'hover') {
            // Barba or an observer callback can run after the pointer
            // has entered. Synchronize the current hover/focus state.
            isHovering = mediaEl.matches(':hover') ||
              mediaEl.matches(':focus-within');
            loadVideo();
          }

          if (shouldResume()) {
            playVideo();
          }
        } else {
          isHovering = false;

          if (!video.paused || shouldBePlaying) {
            setStatus('paused');
            pauseVideo(0, false);
          }
        }
      });
    };

    const handlePageVisibilityChange = () => {
      if (document.hidden) {
        if (!video.paused || shouldBePlaying) {
          setStatus('paused');
          pauseVideo(0, false);
        }
        return;
      }

      if (shouldResume()) {
        playVideo();
      }
    };

    // Register immediately so page transitions cannot drop the first hover.
    // Playback itself remains gated by viewport visibility.
    if (activeMode === 'hover') {
      on(mediaEl, 'mouseenter', handleHoverIn);
      on(mediaEl, 'mouseleave', handleHoverOut);
      on(mediaEl, 'focusin', handleHoverIn);
      on(mediaEl, 'focusout', handleHoverOut);
    }

    mediaEl.dataset.mediaStatus = 'not-active';

    const observer = new IntersectionObserver(
      handleViewport,
      {
        rootMargin: isMobile
          ? '0px'
          : `${rootMarginValue}% 0px ${rootMarginValue}% 0px`,
        threshold: isMobile ? 0.5 : 0
      }
    );

    observer.observe(mediaEl);

    on(video, 'playing', () => {
      if (shouldBePlaying) {
        setStatus('playing');
      }
    });

    on(video, 'error', () => {
      if (video.error) {
        warnPlaybackError(video.error);
      }
    });

    on(video, 'waiting', () => {
      if (shouldBePlaying) {
        setStatus('loading');
      }
    });

    on(video, 'canplay', () => {
      if (shouldBePlaying && !document.hidden) {
        playAttempt();
      }
    });

    on(video, 'loadeddata', () => {
      mediaEl.classList.add('is-video-ready');

      if (shouldBePlaying && !document.hidden) {
        playAttempt();
      }
    });

    on(video, 'ended', () => {
      if (!shouldBePlaying || !isInView || document.hidden) {
        return;
      }

      video.currentTime = 0;
      playAttempt();
    });

    on(
      document,
      'visibilitychange',
      handlePageVisibilityChange
    );

    addCleanup(() => {
      observer.disconnect();
    });

    addCleanup(() => {
      clearPauseTimer();
      shouldBePlaying = false;
      video.pause();
    });
  });

  initMediaSetup._cleanup = cleanupFns;
}

/* =========================================
   HOVER GIFS
   ========================================= */

function initHoverGifs() {
  initHoverGifs._cleanup?.forEach(fn => fn());

  const cleanup = [];

  const interactive = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  ).matches;

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  document.querySelectorAll(
    '.wambolt-system-part [data-hover-gif]'
  ).forEach(mediaEl => {
    const gif = mediaEl.querySelector('[data-hover-gif-src]');
    const toggle = mediaEl.querySelector('[data-media-toggle]');

    if (!gif) return;

    let active = false;
    let inView = false;

    const observer = new IntersectionObserver(
      entries => {
        inView = entries.some(
          entry =>
            entry.target === mediaEl &&
            entry.isIntersecting
        );

        if (!inView) {
          stop();
        } else if (
          interactive &&
          !reducedMotion &&
          (mediaEl.matches(':hover') || mediaEl.matches(':focus-within'))
        ) {
          start();
        }
      },
      {
        rootMargin: '10% 0px 10% 0px',
        threshold: 0
      }
    );

    function setToggle(playing) {
      if (!toggle) return;

      const name = toggle.dataset.motionLabel || 'product';

      toggle.setAttribute(
        'aria-label',
        `${playing ? 'Pause' : 'Play'} ${name} animation`
      );

      toggle.setAttribute('aria-pressed', String(playing));
    }

    function start() {
      if (document.hidden || active) return;

      if (mediaEl.closest(
        '.wambolt-system-part__detail-grid.is-dragging'
      )) {
        return;
      }

      const src = gif.dataset.hoverGifSrc;
      if (!src) return;

      active = true;
      mediaEl.dataset.mediaStatus = 'loading';
      gif.src = src;
      setToggle(true);
    }

    function stop() {
      active = false;
      mediaEl.dataset.mediaStatus = 'not-active';
      gif.removeAttribute('src');
      setToggle(false);
    }

    function toggleMotion() {
      active ? stop() : start();
    }

    function loaded() {
      if (active) {
        mediaEl.dataset.mediaStatus = 'playing';
      }
    }

    function visibility() {
      if (document.hidden) stop();
    }

    if (interactive && !reducedMotion) {
      mediaEl.addEventListener('mouseenter', start);
      mediaEl.addEventListener('mouseleave', stop);
      mediaEl.addEventListener('focusin', start);
      mediaEl.addEventListener('focusout', stop);

      cleanup.push(() => {
        mediaEl.removeEventListener('mouseenter', start);
        mediaEl.removeEventListener('mouseleave', stop);
        mediaEl.removeEventListener('focusin', start);
        mediaEl.removeEventListener('focusout', stop);
      });
    }

    if (toggle) {
      const stopDragPress = event => event.stopPropagation();

      const handleToggleClick = event => {
        event.preventDefault();
        event.stopPropagation();
        toggleMotion();
      };

      toggle.addEventListener('pointerdown', stopDragPress);
      toggle.addEventListener('click', handleToggleClick);

      cleanup.push(() => {
        toggle.removeEventListener('pointerdown', stopDragPress);
        toggle.removeEventListener('click', handleToggleClick);
      });
    }

    gif.addEventListener('load', loaded);

    cleanup.push(() => {
      gif.removeEventListener('load', loaded);
    });

    document.addEventListener('visibilitychange', visibility);

    cleanup.push(() => {
      document.removeEventListener('visibilitychange', visibility);
    });

    observer.observe(mediaEl);

    cleanup.push(() => {
      observer.disconnect();
      stop();
    });
  });

  initHoverGifs._cleanup = cleanup;
}

/* =========================================
   ACCESSIBLE DETAILS NAVIGATION
   ========================================= */

let detailControlCleanups = [];

function destroyDetailControls() {
  detailControlCleanups.forEach(cleanup => cleanup());
  detailControlCleanups = [];
}

function initDetailControls() {
  destroyDetailControls();

  document.querySelectorAll(
    '.wambolt-system-part'
  ).forEach(section => {
    const grid = section.querySelector(
      '.wambolt-system-part__detail-grid'
    );

    const controls = section.querySelector(
      '[data-wambolt-detail-controls]'
    );

    if (!grid || !controls) return;

    const previousButton = controls.querySelector(
      '[data-wambolt-detail-scroll="prev"]'
    );

    const nextButton = controls.querySelector(
      '[data-wambolt-detail-scroll="next"]'
    );

    if (!previousButton || !nextButton) return;

    const cards = Array.from(
      grid.querySelectorAll('.wambolt-system-part__detail')
    );

    const controller = new AbortController();
    const { signal } = controller;

    const getMaxScroll = () => Math.max(
      0,
      grid.scrollWidth - grid.clientWidth
    );

    const updateControls = () => {
      const maxScroll = getMaxScroll();
      const hasOverflow = maxScroll > 2;

      controls.hidden = !hasOverflow;

      if (!hasOverflow) {
        previousButton.disabled = true;
        nextButton.disabled = true;
        return;
      }

      previousButton.disabled = grid.scrollLeft <= 2;
      nextButton.disabled = grid.scrollLeft >= maxScroll - 2;
    };

    const getSnapPositions = () => {
      const maxScroll = getMaxScroll();

      const styles = window.getComputedStyle(grid);

      const snapPadding =
        parseFloat(styles.scrollPaddingLeft) ||
        parseFloat(styles.paddingLeft) ||
        0;

      const gridLeft = grid.getBoundingClientRect().left;
      const currentScroll = grid.scrollLeft;

      const positions = [0, maxScroll];

      cards.forEach(card => {
        const cardLeft =
          card.getBoundingClientRect().left -
          gridLeft +
          currentScroll -
          snapPadding;

        const position = Math.max(
          0,
          Math.min(maxScroll, cardLeft)
        );

        positions.push(position);
      });

      return [
        ...new Set(positions.map(Math.round))
      ].sort((a, b) => a - b);
    };

    const navigate = direction => {
      const currentScroll = grid.scrollLeft;
      const positions = getSnapPositions();

      const target = direction > 0
        ? positions.find(position => position > currentScroll + 3)
        : positions.findLast(position => position < currentScroll - 3);

      if (target === undefined) return;

      /*
       * Stop any active GSAP drag/inertia tween
       * before moving the native scroll position.
       */
      grid.dispatchEvent(
        new CustomEvent('wambolt:detail-nav')
      );

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      grid.scrollTo({
        left: target,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    };

    previousButton.addEventListener(
      'click',
      () => navigate(-1),
      { signal }
    );

    nextButton.addEventListener(
      'click',
      () => navigate(1),
      { signal }
    );

    grid.addEventListener(
      'scroll',
      updateControls,
      { passive: true, signal }
    );

    window.addEventListener(
      'resize',
      updateControls,
      { passive: true, signal }
    );

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateControls)
      : null;

    if (resizeObserver) {
      resizeObserver.observe(grid);
    }

    detailControlCleanups.push(() => {
      controller.abort();
      resizeObserver?.disconnect();
    });

    updateControls();
  });
}

/* =========================================
   GSAP DETAILS DRAG
   ========================================= */

let detailDragRetryTimer = null;
let detailDragRetryAttempts = 0;
let detailDragCleanups = [];

function destroyDetailDrag() {
  if (detailDragRetryTimer !== null) {
    clearTimeout(detailDragRetryTimer);
    detailDragRetryTimer = null;
  }

  detailDragCleanups.forEach(cleanup => cleanup());
  detailDragCleanups = [];
}

function initDetailDrag() {
  destroyDetailDrag();

  const grids = document.querySelectorAll(
    '.wambolt-system-part__detail-grid'
  );

  if (!grids.length) {
    detailDragRetryAttempts = 0;
    return;
  }

  if (!window.gsap || !window.Draggable || !window.InertiaPlugin) {
    if (detailDragRetryAttempts < 40) {
      detailDragRetryAttempts += 1;
      detailDragRetryTimer = setTimeout(initDetailDrag, 100);
    }
    return;
  }

  detailDragRetryAttempts = 0;

  const gsap = window.gsap;
  const Draggable = window.Draggable;

  gsap.registerPlugin(Draggable, window.InertiaPlugin);

  const useInertia = !window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  grids.forEach(grid => {
    if (grid.scrollWidth <= grid.clientWidth + 1) return;

    const proxy = document.createElement('div');

    let startScroll = 0;
    let maxScroll = 0;
    let snapStops = [];
    let didDrag = false;

    grid.classList.add('is-draggable');

    const clampScroll = value => Math.max(
      0,
      Math.min(maxScroll, value)
    );

    const measureSnapStops = () => {
      const styles = window.getComputedStyle(grid);

      const gutter =
        parseFloat(styles.scrollPaddingLeft) ||
        parseFloat(styles.paddingLeft) ||
        0;

      const gridLeft = grid.getBoundingClientRect().left;
      const current = grid.scrollLeft;
      const positions = [0, maxScroll];

      grid.querySelectorAll('.wambolt-system-part__detail')
        .forEach(card => {
          const left = card.getBoundingClientRect().left
            - gridLeft + current - gutter;

          positions.push(clampScroll(left));
        });

      snapStops = [...new Set(positions.map(Math.round))];
    };

    const closestSnap = value => snapStops.reduce(
      (closest, option) =>
        Math.abs(option - value) < Math.abs(closest - value)
          ? option
          : closest,
      snapStops[0] ?? 0
    );

    const syncScroll = function () {
      grid.scrollLeft = clampScroll(startScroll - this.x);
    };

    const endDrag = () => {
      grid.classList.remove('is-dragging');
    };

    const settleWithoutThrow = () => {
      if (!didDrag) {
        endDrag();
        return;
      }

      const destination = closestSnap(grid.scrollLeft);

      if (!useInertia || Math.abs(destination - grid.scrollLeft) < 2) {
        grid.scrollLeft = destination;
        endDrag();
        return;
      }

      gsap.to(grid, {
        scrollLeft: destination,
        duration: 0.18,
        ease: 'power2.out',
        overwrite: true,
        onComplete: endDrag
      });
    };

    const draggable = Draggable.create(proxy, {
      trigger: grid,
      type: 'x',
      dragClickables: false,
      allowContextMenu: true,
      minimumMovement: 7,
      inertia: useInertia,

      throwResistance: 6500,
      minDuration: 0.12,
      maxDuration: 0.45,
      overshootTolerance: 0,

      snap: {
        x(value) {
          const projectedScroll = clampScroll(startScroll - value);
          return startScroll - closestSnap(projectedScroll);
        }
      },

      onPressInit() {
        this.tween?.kill();
        gsap.killTweensOf(proxy);
        gsap.killTweensOf(grid);

        gsap.set(proxy, { x: 0 });
        this.update();

        startScroll = grid.scrollLeft;

        maxScroll = Math.max(
          0,
          grid.scrollWidth - grid.clientWidth
        );

        measureSnapStops();
        didDrag = false;

        this.applyBounds({
          minX: startScroll - maxScroll,
          maxX: startScroll
        });

        grid.classList.add('is-dragging');
      },

      onDragStart() {
        didDrag = true;

        grid.querySelectorAll('[data-media-init], [data-hover-gif]')
          .forEach(media => {
            media.dispatchEvent(new MouseEvent('mouseleave'));
          });
      },

      onDrag: syncScroll,
      onThrowUpdate: syncScroll,

      onRelease() {
        if (!didDrag || !this.isThrowing) {
          settleWithoutThrow();
        }
      },

      onThrowComplete: endDrag
    })[0];

    if (!draggable) {
      grid.classList.remove('is-draggable');
      return;
    }

    const stopThrow = () => {
      draggable.tween?.kill();
      gsap.killTweensOf(proxy);
      gsap.killTweensOf(grid);
      endDrag();
    };

    const preventImageDrag = event => {
      if (event.target.closest('img')) {
        event.preventDefault();
      }
    };

    grid.addEventListener('wheel', stopThrow, { passive: true });
    grid.addEventListener('keydown', stopThrow);
    grid.addEventListener('dragstart', preventImageDrag);
    grid.addEventListener('wambolt:detail-nav', stopThrow);

    detailDragCleanups.push(() => {
      grid.removeEventListener('wheel', stopThrow);
      grid.removeEventListener('keydown', stopThrow);
      grid.removeEventListener('dragstart', preventImageDrag);
      grid.removeEventListener('wambolt:detail-nav', stopThrow);

      stopThrow();
      draggable.kill();
      grid.classList.remove('is-draggable');
    });
  });
}

/* =========================================
   SHOPIFY AND BARBA LIFECYCLE
   ========================================= */

function initSystemPartMotion() {
  initMediaSetup();
  initHoverGifs();
  initDetailDrag();
  initDetailControls();
}

function destroySystemPartMotion() {
  destroyDetailControls();
  destroyDetailDrag();

  initMediaSetup._cleanup?.forEach(fn => fn());
  initMediaSetup._cleanup = [];

  initHoverGifs._cleanup?.forEach(fn => fn());
  initHoverGifs._cleanup = [];
}

document.addEventListener(
  'shopify:section:load',
  initSystemPartMotion
);

document.addEventListener(
  'shopify:section:unload',
  () => queueMicrotask(initSystemPartMotion)
);

document.addEventListener(
  'wambolt:page:before-leave',
  destroySystemPartMotion
);

document.addEventListener(
  'wambolt:page:enter',
  initSystemPartMotion
);

const mobilePlaybackBreakpoint = window.matchMedia('(max-width: 749px)');

if (typeof mobilePlaybackBreakpoint.addEventListener === 'function') {
  mobilePlaybackBreakpoint.addEventListener('change', initMediaSetup);
} else if (typeof mobilePlaybackBreakpoint.addListener === 'function') {
  mobilePlaybackBreakpoint.addListener(initMediaSetup);
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initSystemPartMotion,
    { once: true }
  );
} else {
  initSystemPartMotion();
}
